import { LazyPreviewService } from 'src/app/services/lazy-preview.service';
/**
 * Header Email Sidebar Component
 * A comprehensive email management sidebar triggered from the header mail icon
 *
 * Features:
 * - Quick email list view with inbox, unread, flagged tabs
 * - Real-time email notifications
 * - Quick actions (read/unread, flag, delete)
 * - Search functionality
 * - Compose new email
 * - Navigate to full email module
 */

import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  ViewEncapsulation,
  HostListener,
  ElementRef,
  ViewChild, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, combineLatest, Observable, of } from 'rxjs';
import { takeUntil, map, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

import {
  HeaderEmailSidebarService,
  IQuickEmail,
  IEmailNotification,
  EmailSidebarTab
} from './header-email-sidebar.service';
import { EmailDataService } from '../../../email/email.data.service';
import { ToasterService } from '../../../../services/core/toaster.service';
import { IUserAccount } from '../../../email/data/email-dummy-data';
import { IEmailFolder, FOLDER_ICONS } from '../../../email/interfaces/email-folder.interface';
import { IEmailAttachment } from '../../../email/interfaces/email-message.interface';
import { MatDialog } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
// Shared preview components (unified architecture)
import { DocumentImageAnnotatorComponent } from '../../../document/document-image-annotator/document-image-annotator.component';
import { PdfPreviewComponent } from '../../pdf-preview/pdf-preview.component';
import { AudioPreviewComponent } from '../../audio-preview/audio-preview.component';
import { VideoPreviewComponent } from '../../video-preview/video-preview.component';

@Component({
    selector: 'app-header-email-sidebar',
    templateUrl: './header-email-sidebar.component.html',
    styleUrls: ['./header-email-sidebar.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class HeaderEmailSidebarComponent implements OnInit, OnDestroy {
  private readonly lazyPreview = inject(LazyPreviewService);
  @Input() isOpen: boolean = false;
  @Output() onClose = new EventEmitter<void>();
  @Output() onEmailClick = new EventEmitter<IQuickEmail>();

  // State
  emails: IQuickEmail[] = [];
  notifications: IEmailNotification[] = [];
  activeTab: EmailSidebarTab = 'inbox';
  searchQuery: string = '';
  isLoading: boolean = false;

  // Counts
  unreadCount: number = 0;
  flaggedCount: number = 0;
  notificationCount: number = 0;

  // UI State
  showSearchBar: boolean = false;
  hoveredEmailId: number | null = null;

  // Account Management State
  userAccounts: IUserAccount[] = [];
  activeAccount: IUserAccount | null = null;
  showAccountSelector: boolean = false;

  // Folder Navigation State
  activeFolder: IEmailFolder | null = null;
  showFolderTree: boolean = true;
  expandedFolders: Set<number> = new Set();

  // Reading Pane State
  selectedEmail: IQuickEmail | null = null;
  viewMode: 'list' | 'reading' | 'compose' = 'list';

  // Compose State
  composeMode: 'compose' | 'reply' | 'replyAll' | 'forward' = 'compose';
  composeEmail: IQuickEmail | null = null;
  composeForm = {
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body: ''
  };

  // Compose UI State
  showCC: boolean = false;
  showBCC: boolean = false;
  priority: 'normal' | 'high' = 'normal';
  isSending: boolean = false;

  // Attachments
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  attachments: File[] = [];
  maxAttachmentSize = 25 * 1024 * 1024; // 25MB total

  // Recipient Autocomplete
  recipientOptions$: Observable<any[]> = of([]);
  recipientSearchInput$ = new Subject<string>();
  toRecipients: string[] = [];
  ccRecipients: string[] = [];
  bccRecipients: string[] = [];

  // =============================================================================
  // EMOJI PICKER STATE
  // =============================================================================
  showEmojiPicker: boolean = false;
  emojiSearchTerm: string = '';
  filteredEmojis: { char: string; name: string }[] = [];
  allEmojis: { char: string; name: string }[] = [
    { char: '😀', name: 'grinning' }, { char: '😃', name: 'smiley' }, { char: '😄', name: 'smile' },
    { char: '😁', name: 'grin' }, { char: '😆', name: 'laughing' }, { char: '😅', name: 'sweat_smile' },
    { char: '🤣', name: 'rofl' }, { char: '😂', name: 'joy' }, { char: '🙂', name: 'slightly_smiling' },
    { char: '😊', name: 'blush' }, { char: '😇', name: 'innocent' }, { char: '😉', name: 'wink' },
    { char: '😍', name: 'heart_eyes' }, { char: '🥰', name: 'smiling_hearts' }, { char: '😘', name: 'kissing' },
    { char: '👍', name: 'thumbsup' }, { char: '👎', name: 'thumbsdown' }, { char: '👏', name: 'clap' },
    { char: '🙌', name: 'raised_hands' }, { char: '🤝', name: 'handshake' }, { char: '✅', name: 'check' },
    { char: '❌', name: 'x' }, { char: '⭐', name: 'star' }, { char: '🔥', name: 'fire' },
    { char: '💯', name: 'hundred' }, { char: '💪', name: 'muscle' }, { char: '🎉', name: 'tada' },
    { char: '📧', name: 'email' }, { char: '📎', name: 'paperclip' }, { char: '📅', name: 'calendar' },
    { char: '⏰', name: 'alarm_clock' }, { char: '💼', name: 'briefcase' }, { char: '📊', name: 'chart' },
    { char: '✨', name: 'sparkles' }, { char: '💡', name: 'bulb' }, { char: '🚀', name: 'rocket' },
  ];

  // =============================================================================
  // FORMATTING STATE
  // =============================================================================
  activeFormats: { bold: boolean; italic: boolean; underline: boolean } = {
    bold: false, italic: false, underline: false
  };

  // =============================================================================
  // ENTITY LINKING STATE
  // =============================================================================
  linkedJob: { id: number; name: string } | null = null;
  linkedCustomer: { id: number; name: string } | null = null;
  linkedTicket: { id: number; code: string; name: string } | null = null;
  showEntityLinkPanel: boolean = false;
  entityLinkType: 'job' | 'customer' | 'ticket' | null = null;

  // Entity search observables
  jobOptions$: Observable<any[]> = of([]);
  customerOptions$: Observable<any[]> = of([]);
  ticketOptions$: Observable<any[]> = of([]);
  jobSearchInput$ = new Subject<string>();
  customerSearchInput$ = new Subject<string>();
  ticketSearchInput$ = new Subject<string>();

  // =============================================================================
  // LABELS STATE
  // =============================================================================
  availableLabels: any[] = [];
  selectedLabels: any[] = [];
  showLabelPicker: boolean = false;

  // =============================================================================
  // SIGNATURE STATE
  // =============================================================================
  availableSignatures: any[] = [];
  currentSignature: any | null = null;
  showSignatureSelector: boolean = false;

  // =============================================================================
  // SCHEDULE SEND STATE
  // =============================================================================
  isScheduled: boolean = false;
  scheduledDate: Date | null = null;
  scheduledTime: string = '';
  showSchedulePicker: boolean = false;
  minScheduleDate: Date = new Date();

  // =============================================================================
  // DRAFT STATE
  // =============================================================================
  isSavingDraft: boolean = false;

  private destroy$ = new Subject<void>();

  private apiUrl = `${environment.apiBaseUrl}${environment.apiUrl}`;

  constructor(
    private emailSidebarService: HeaderEmailSidebarService,
    private emailDataService: EmailDataService,
    private toasterService: ToasterService,
    private router: Router,
    private elementRef: ElementRef,
    private dialog: MatDialog,
    private http: HttpClient
  ) {}

  // Avatar color palette (matches email module's email-layout.component.ts)
  private readonly AVATAR_COLORS = [
    '#FF6600', '#28304E', '#4CAF50', '#2196F3', '#9C27B0',
    '#FF5722', '#607D8B', '#795548', '#E91E63', '#00BCD4',
    '#0097A7', '#00897B', '#00ACC1', '#039BE5', '#3949AB'
  ];

  /**
   * Get initials from email address (first 2 chars of local part).
   * Matches email module's email-layout.getAccountInitials()
   */
  public getAccountInitials(email?: string): string {
    if (!email) return '?';
    const local = email.split('@')[0];
    return local.substring(0, 2).toUpperCase();
  }

  /**
   * Get consistent avatar background color based on a string (email or name).
   * Matches email module's email-layout.getAvatarColor()
   */
  public getAccountAvatarColor(str?: string): string {
    if (!str) return this.AVATAR_COLORS[0];
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return this.AVATAR_COLORS[Math.abs(hash) % this.AVATAR_COLORS.length];
  }

  ngOnInit(): void {
    this.initializeSubscriptions();
    this.initializeAccounts();
    this.setupRecipientSearch();
    this.setupEntitySearch();
    this.loadLabels();
    this.loadSignatures();
    this.filteredEmojis = [...this.allEmojis];
  }

  private initializeAccounts(): void {
    // Initialize accounts from service (async API call)
    this.emailSidebarService.initializeAccounts();

    // Subscribe to active account changes — also refresh local accounts list
    this.emailSidebarService.activeAccount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(account => {
        this.activeAccount = account;
        this.userAccounts = this.emailSidebarService.userAccounts;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeSubscriptions(): void {
    // Subscribe to loading state
    this.emailSidebarService.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => {
        this.isLoading = loading;
      });

    // Subscribe to active tab
    this.emailSidebarService.activeTab$
      .pipe(takeUntil(this.destroy$))
      .subscribe(tab => {
        this.activeTab = tab;
      });

    // Subscribe to counts
    this.emailSidebarService.unreadCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.unreadCount = count;
      });

    this.emailSidebarService.flaggedCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.flaggedCount = count;
      });

    this.emailSidebarService.notificationCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.notificationCount = count;
      });

    // Subscribe to notifications
    this.emailSidebarService.notifications$
      .pipe(takeUntil(this.destroy$))
      .subscribe(notifications => {
        this.notifications = notifications;
      });

    // Keep local activeFolder in sync with service (covers auto-select on load)
    this.emailSidebarService.activeFolder$
      .pipe(takeUntil(this.destroy$))
      .subscribe(folder => {
        this.activeFolder = folder;
      });

    // Subscribe to filtered emails based on active tab and search
    combineLatest([
      this.emailSidebarService.emails$,
      this.emailSidebarService.activeTab$,
      this.emailSidebarService.searchQuery$
    ]).pipe(
      takeUntil(this.destroy$),
      map(([emails, tab, query]) => this.filterEmails(emails, tab, query))
    ).subscribe(filteredEmails => {
      this.emails = filteredEmails;
    });

    // Subscribe to new email notifications for toast/sound
    this.emailSidebarService.newEmail$
      .pipe(takeUntil(this.destroy$))
      .subscribe(email => {
        this.handleNewEmailNotification(email);
      });
  }

  private filterEmails(emails: IQuickEmail[], tab: EmailSidebarTab, query: string): IQuickEmail[] {
    let filtered = emails;

    // Filter by tab
    switch (tab) {
      case 'unread':
        filtered = filtered.filter(e => !e.isRead);
        break;
      case 'flagged':
        filtered = filtered.filter(e => e.isFlagged);
        break;
      case 'notifications':
        return []; // Notifications tab shows notifications, not emails
    }

    // Filter by search query
    if (query) {
      const lowerQuery = query.toLowerCase();
      filtered = filtered.filter(e =>
        e.senderName.toLowerCase().includes(lowerQuery) ||
        e.senderEmail.toLowerCase().includes(lowerQuery) ||
        e.subject.toLowerCase().includes(lowerQuery) ||
        e.preview.toLowerCase().includes(lowerQuery)
      );
    }

    return filtered;
  }

  private handleNewEmailNotification(email: IQuickEmail): void {
    // Play notification sound (optional)
    // this.playNotificationSound();

    // Show browser notification if permitted
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(`New email from ${email.senderName}`, {
        body: email.subject,
        icon: '/assets/img/email-icon.png'
      });
    }
  }

  // ==================== TAB ACTIONS ====================

  setActiveTab(tab: EmailSidebarTab): void {
    this.emailSidebarService.setActiveTab(tab);
  }

  // ==================== SEARCH ACTIONS ====================

  toggleSearchBar(): void {
    this.showSearchBar = !this.showSearchBar;
    if (!this.showSearchBar) {
      this.clearSearch();
    }
  }

  onSearchChange(query: string): void {
    this.searchQuery = query;
    this.emailSidebarService.setSearchQuery(query);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.emailSidebarService.clearSearch();
  }

  // ==================== EMAIL ACTIONS ====================

  onEmailItemClick(email: IQuickEmail, event: Event): void {
    // Stop propagation to prevent document click handler from closing sidebar
    event.stopPropagation();
    // Open email in reading pane within sidebar
    this.openEmailInReadingPane(email);
  }

  // ==================== READING PANE ACTIONS ====================

  // Thread messages for conversation view
  threadMessages: IQuickEmail[] = [];
  isLoadingThread: boolean = false;

  openEmailInReadingPane(email: IQuickEmail): void {
    this.selectedEmail = email;
    this.viewMode = 'reading';
    this.threadMessages = [];

    // Mark as read
    if (!email.isRead) {
      this.emailSidebarService.markAsRead(email.id);
    }

    // Same logic as email module's email-content-pane.component.ts:
    // If thread_id exists → always call thread API (inbox list count doesn't include
    // outbox replies, so we can't rely on thread_message_count alone).
    // If no thread_id → lazy-load body via /emails/body/{id}
    if (email.threadId) {
      this.loadThread(email.threadId, email);
    } else {
      this.loadEmailBody(email);
    }

    // Emit event for parent to handle (optional tracking)
    this.onEmailClick.emit(email);
  }

  /**
   * Load full thread via GET /emails/thread/{threadId}
   * Mirrors email module's email-content-pane loadThreadForEmail():
   *   - If response has multiple emails → show conversation view
   *   - If response has single email → use its body_html for single view
   *   - On error → fallback to lazy-loading body
   */
  private loadThread(threadId: string, fallbackEmail: IQuickEmail): void {
    this.isLoadingThread = true;
    this.emailSidebarService.fetchThread(threadId).subscribe({
      next: (messages: IQuickEmail[]) => {
        if (messages.length > 1) {
          // Multiple messages — show conversation view (same as email module)
          this.threadMessages = messages;
        } else if (messages.length === 1) {
          // Single message in thread — use its body (thread API includes body_html)
          this.selectedEmail = { ...fallbackEmail, message: messages[0].message };
        } else {
          // Empty — fallback to body API
          this.loadEmailBody(fallbackEmail);
        }
        this.isLoadingThread = false;
      },
      error: () => {
        // Thread API failed — fallback to body API
        this.loadEmailBody(fallbackEmail);
      }
    });
  }

  /**
   * Lazy-load email body when no thread_id exists.
   * Uses GET /emails/body/{emailId}?type=inbox|outbox
   * (body_html is excluded from inbox list API for performance)
   */
  private loadEmailBody(email: IQuickEmail): void {
    if (email.message && email.message.length > 200) return; // Already has body
    this.isLoadingThread = true;
    const emailType = email.emailType || 'inbox';
    this.emailSidebarService.fetchEmailBody(email.id, emailType).subscribe({
      next: (body: string) => {
        this.selectedEmail = { ...email, message: body };
        this.isLoadingThread = false;
      },
      error: () => {
        this.isLoadingThread = false;
      }
    });
  }

  backToList(event: Event): void {
    event.stopPropagation();
    this.selectedEmail = null;
    this.threadMessages = [];
    this.viewMode = 'list';
  }

  // ==================== COMPOSE ACTIONS ====================

  openCompose(mode: 'compose' | 'reply' | 'replyAll' | 'forward', email?: IQuickEmail): void {
    this.composeMode = mode;
    this.composeEmail = email || null;
    this.viewMode = 'compose';
    this.prepareComposeForm();
  }

  prepareComposeForm(): void {
    // Reset UI state
    this.showCC = false;
    this.showBCC = false;
    this.priority = 'normal';
    this.attachments = [];
    this.toRecipients = [];
    this.ccRecipients = [];
    this.bccRecipients = [];

    if (this.composeMode === 'reply' && this.composeEmail) {
      this.composeForm.to = this.composeEmail.senderEmail;
      this.toRecipients = [this.composeEmail.senderEmail];
      this.composeForm.cc = '';
      this.composeForm.bcc = '';
      this.composeForm.subject = `Re: ${this.composeEmail.subject}`;
      this.composeForm.body = this.getQuotedBody();
    } else if (this.composeMode === 'replyAll' && this.composeEmail) {
      this.composeForm.to = this.composeEmail.senderEmail;
      this.toRecipients = [this.composeEmail.senderEmail];
      const ccList = (this.composeEmail.ccRecipients || []).map((r: any) => r.email || r);
      this.composeForm.cc = ccList.join(', ');
      this.ccRecipients = ccList;
      this.composeForm.bcc = '';
      this.composeForm.subject = `Re: ${this.composeEmail.subject}`;
      this.composeForm.body = this.getQuotedBody();
      if (ccList.length > 0) {
        this.showCC = true;
      }
    } else if (this.composeMode === 'forward' && this.composeEmail) {
      this.composeForm.to = '';
      this.composeForm.cc = '';
      this.composeForm.bcc = '';
      this.composeForm.subject = `Fwd: ${this.composeEmail.subject}`;
      this.composeForm.body = this.getForwardedBody();
    } else {
      // New compose
      this.composeForm = { to: '', cc: '', bcc: '', subject: '', body: '' };
    }
  }

  private setupRecipientSearch(): void {
    this.recipientOptions$ = this.recipientSearchInput$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term => term.length >= 2 ? this.emailDataService.searchRecipients(term) : of([])),
      takeUntil(this.destroy$)
    );
  }

  onRecipientSearch(term: string): void {
    this.recipientSearchInput$.next(term);
  }

  getQuotedBody(): string {
    const email = this.composeEmail!;
    return `\n\n---\nOn ${email.timeAgo}, ${email.senderName} <${email.senderEmail}> wrote:\n\n${email.message || email.preview}`;
  }

  getForwardedBody(): string {
    const email = this.composeEmail!;
    return `\n\n---------- Forwarded message ----------\nFrom: ${email.senderName} <${email.senderEmail}>\nDate: ${email.timeAgo}\nSubject: ${email.subject}\n\n${email.message || email.preview}`;
  }

  backFromCompose(event: Event): void {
    event.stopPropagation();
    // Go back to reading pane if replying to an email, otherwise go to list
    if (this.composeEmail && (this.composeMode === 'reply' || this.composeMode === 'replyAll')) {
      this.viewMode = 'reading';
      this.selectedEmail = this.composeEmail;
    } else {
      this.viewMode = 'list';
    }
    this.resetComposeState();
  }

  sendEmail(event: Event): void {
    event.stopPropagation();

    // Validate
    if (!this.composeForm.to.trim()) {
      this.toasterService.showWarning('Please enter at least one recipient');
      return;
    }
    if (!this.composeForm.subject.trim()) {
      this.toasterService.showWarning('Please enter a subject');
      return;
    }

    if (!this.activeAccount) {
      this.toasterService.showWarning('No active email account');
      return;
    }

    this.isSending = true;

    const accountId = (this.activeAccount as any).accountId || this.activeAccount.id;
    const isReply = (this.composeMode === 'reply' || this.composeMode === 'replyAll') && this.composeEmail;

    const formData = new FormData();
    let endpoint: string;

    if (isReply) {
      // Reply/ReplyAll → POST /emails/reply with inbox_id
      // Backend uses original message_id to set In-Reply-To/References headers
      // so the email provider groups it as a thread
      endpoint = `${this.apiUrl}/emails/reply`;
      formData.append('inbox_id', this.composeEmail!.id.toString());
      formData.append('body', this.composeForm.body.replace(/\n/g, '<br>'));
      formData.append('mode', this.composeMode);

      const toEmails = this.composeForm.to.split(',').map((e: string) => e.trim()).filter(Boolean);
      formData.append('to', JSON.stringify(toEmails));

      if (this.showCC && this.composeForm.cc.trim()) {
        const ccEmails = this.composeForm.cc.split(',').map((e: string) => e.trim()).filter(Boolean);
        formData.append('cc', JSON.stringify(ccEmails));
      }
      if (this.showBCC && this.composeForm.bcc.trim()) {
        const bccEmails = this.composeForm.bcc.split(',').map((e: string) => e.trim()).filter(Boolean);
        formData.append('bcc', JSON.stringify(bccEmails));
      }
    } else {
      // Compose/Forward → POST /emails/send as new email
      endpoint = `${this.apiUrl}/emails/send`;
      formData.append('email_account_id', accountId.toString());
      formData.append('to', this.composeForm.to);
      formData.append('subject', this.composeForm.subject);
      formData.append('body', this.composeForm.body.replace(/\n/g, '<br>'));

      if (this.showCC && this.composeForm.cc.trim()) {
        formData.append('cc', this.composeForm.cc);
      }
      if (this.showBCC && this.composeForm.bcc.trim()) {
        formData.append('bcc', this.composeForm.bcc);
      }

      // Entity linking (only for new emails)
      if (this.linkedJob) {
        formData.append('module_id', '19');
        formData.append('module_data_id', this.linkedJob.id.toString());
      } else if (this.linkedCustomer) {
        formData.append('module_id', '13');
        formData.append('module_data_id', this.linkedCustomer.id.toString());
      } else if (this.linkedTicket) {
        formData.append('module_id', '3');
        formData.append('module_data_id', this.linkedTicket.id.toString());
      }
    }

    // Attachments (both modes)
    if (this.attachments.length > 0) {
      this.attachments.forEach((file: File, index: number) => {
        formData.append(`attachments[${index}]`, file, file.name);
      });
    }

    this.http.post(endpoint, formData).subscribe({
      next: (response: any) => {
        this.isSending = false;
        this.toasterService.showSuccessMessage('Email sent successfully');
        this.viewMode = 'list';
        this.resetComposeState();
        // Refresh email list to show sent email
        this.emailSidebarService.refreshEmails();
      },
      error: (err) => {
        this.isSending = false;
        this.toasterService.showError('Failed to send email. Please try again.');
        console.error('Send email error:', err);
      }
    });
  }

  private resetComposeState(): void {
    this.composeEmail = null;
    this.composeForm = { to: '', cc: '', bcc: '', subject: '', body: '' };
    this.showCC = false;
    this.showBCC = false;
    this.priority = 'normal';
    this.attachments = [];
    this.toRecipients = [];
    this.ccRecipients = [];
    this.bccRecipients = [];

    // Reset emoji picker
    this.showEmojiPicker = false;
    this.emojiSearchTerm = '';
    this.filteredEmojis = [...this.allEmojis];

    // Reset formatting
    this.activeFormats = { bold: false, italic: false, underline: false };

    // Reset entity linking
    this.linkedJob = null;
    this.linkedCustomer = null;
    this.linkedTicket = null;
    this.showEntityLinkPanel = false;
    this.entityLinkType = null;

    // Reset labels
    this.selectedLabels = [];
    this.showLabelPicker = false;

    // Reset signature
    this.currentSignature = null;
    this.showSignatureSelector = false;

    // Reset schedule
    this.isScheduled = false;
    this.scheduledDate = null;
    this.scheduledTime = '';
    this.showSchedulePicker = false;
  }

  private parseRecipients(recipientString: string): { name: string; email: string }[] {
    if (!recipientString.trim()) return [];
    return recipientString.split(',').map(email => ({
      name: email.trim(),
      email: email.trim()
    })).filter(r => this.isValidEmail(r.email));
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // ==================== CC/BCC TOGGLE ====================

  toggleCC(): void {
    this.showCC = !this.showCC;
    if (!this.showCC) {
      this.composeForm.cc = '';
      this.ccRecipients = [];
    }
  }

  toggleBCC(): void {
    this.showBCC = !this.showBCC;
    if (!this.showBCC) {
      this.composeForm.bcc = '';
      this.bccRecipients = [];
    }
  }

  // ==================== PRIORITY ====================

  togglePriority(): void {
    this.priority = this.priority === 'normal' ? 'high' : 'normal';
  }

  // ==================== ATTACHMENTS ====================

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;

    const newFiles = Array.from(input.files);
    const currentSize = this.getTotalAttachmentSize();
    const newSize = newFiles.reduce((sum, f) => sum + f.size, 0);

    if (currentSize + newSize > this.maxAttachmentSize) {
      this.toasterService.showError(`Total attachments cannot exceed ${this.formatFileSize(this.maxAttachmentSize)}`);
      return;
    }

    this.attachments.push(...newFiles);
    input.value = ''; // Reset input
  }

  removeAttachment(index: number): void {
    this.attachments.splice(index, 1);
  }

  getTotalAttachmentSize(): number {
    return this.attachments.reduce((total, file) => total + file.size, 0);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileIcon(type: string): string {
    if (type.includes('pdf')) return 'picture_as_pdf';
    if (type.includes('image')) return 'image';
    if (type.includes('video')) return 'videocam';
    if (type.includes('audio')) return 'audiotrack';
    if (type.includes('word') || type.includes('document')) return 'description';
    if (type.includes('excel') || type.includes('spreadsheet')) return 'table_chart';
    return 'attach_file';
  }

  triggerFileInput(): void {
    this.fileInput?.nativeElement?.click();
  }

  get canSend(): boolean {
    return !!this.composeForm.to.trim() && !!this.composeForm.subject.trim();
  }

  // ==================== ENTITY SEARCH SETUP ====================

  private setupEntitySearch(): void {
    // Job search
    this.jobOptions$ = this.jobSearchInput$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term => term.length >= 2 ? this.emailDataService.searchJobs(term) : of([])),
      takeUntil(this.destroy$)
    );

    // Customer search
    this.customerOptions$ = this.customerSearchInput$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term => term.length >= 2 ? this.emailDataService.searchCustomers(term) : of([])),
      takeUntil(this.destroy$)
    );

    // Ticket search
    this.ticketOptions$ = this.ticketSearchInput$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term => term.length >= 2 ? this.emailDataService.searchTickets(term) : of([])),
      takeUntil(this.destroy$)
    );
  }

  // ==================== LABELS LOADING ====================

  private loadLabels(): void {
    this.emailDataService.getLabels().pipe(takeUntil(this.destroy$)).subscribe({
      next: (labels) => {
        this.availableLabels = labels;
      },
      error: () => {
        // Use default labels if API fails
        this.availableLabels = [
          { id: 1, name: 'Important', color: '#f44336' },
          { id: 2, name: 'Work', color: '#2196f3' },
          { id: 3, name: 'Personal', color: '#4caf50' },
          { id: 4, name: 'Follow-up', color: '#ff9800' },
          { id: 5, name: 'Urgent', color: '#e91e63' },
        ];
      }
    });
  }

  // ==================== SIGNATURE LOADING ====================

  private loadSignatures(): void {
    // Default signatures - can be replaced with API call
    this.availableSignatures = [
      { id: 1, name: 'Default Signature', contentHtml: '<p>Best regards,<br>Your Name</p>' },
      { id: 2, name: 'Formal Signature', contentHtml: '<p>Sincerely,<br>Your Name<br>Company Name</p>' },
    ];
  }

  // ==================== TEXT FORMATTING ====================

  formatBold(): void {
    this.activeFormats.bold = !this.activeFormats.bold;
    document.execCommand('bold', false);
  }

  formatItalic(): void {
    this.activeFormats.italic = !this.activeFormats.italic;
    document.execCommand('italic', false);
  }

  formatUnderline(): void {
    this.activeFormats.underline = !this.activeFormats.underline;
    document.execCommand('underline', false);
  }

  formatBullets(): void {
    document.execCommand('insertUnorderedList', false);
  }

  formatNumbers(): void {
    document.execCommand('insertOrderedList', false);
  }

  insertLink(): void {
    const url = prompt('Enter URL:');
    if (url) {
      document.execCommand('createLink', false, url);
    }
  }

  // ==================== EMOJI PICKER ====================

  toggleEmojiPicker(): void {
    this.showEmojiPicker = !this.showEmojiPicker;
    if (this.showEmojiPicker) {
      this.filteredEmojis = [...this.allEmojis];
      this.emojiSearchTerm = '';
      // Close other pickers
      this.closeAllPickers('emoji');
    }
  }

  searchEmoji(event: Event): void {
    const term = (event.target as HTMLInputElement).value.toLowerCase();
    if (!term) {
      this.filteredEmojis = [...this.allEmojis];
    } else {
      this.filteredEmojis = this.allEmojis.filter(e => e.name.includes(term));
    }
  }

  insertEmoji(emoji: { char: string; name: string }): void {
    this.composeForm.body += emoji.char;
    this.showEmojiPicker = false;
  }

  // ==================== ENTITY LINKING ====================

  openEntityLinkDialog(type: 'job' | 'customer' | 'ticket'): void {
    this.entityLinkType = type;
    this.showEntityLinkPanel = true;
    this.closeAllPickers('entity');
  }

  closeEntityLinkPanel(): void {
    this.showEntityLinkPanel = false;
    this.entityLinkType = null;
  }

  onEntitySearch(term: string): void {
    switch (this.entityLinkType) {
      case 'job':
        this.jobSearchInput$.next(term);
        break;
      case 'customer':
        this.customerSearchInput$.next(term);
        break;
      case 'ticket':
        this.ticketSearchInput$.next(term);
        break;
    }
  }

  selectEntity(entity: any): void {
    if (!entity) return;

    switch (this.entityLinkType) {
      case 'job':
        this.linkedJob = { id: entity.id, name: entity.job_name || entity.name };
        break;
      case 'customer':
        this.linkedCustomer = { id: entity.id, name: entity.company_name || entity.name };
        break;
      case 'ticket':
        this.linkedTicket = {
          id: entity.id,
          code: entity.ticket_code || entity.code || `#${entity.id}`,
          name: entity.name
        };
        break;
    }
    this.closeEntityLinkPanel();
  }

  removeEntityLink(type: 'job' | 'customer' | 'ticket'): void {
    switch (type) {
      case 'job':
        this.linkedJob = null;
        break;
      case 'customer':
        this.linkedCustomer = null;
        break;
      case 'ticket':
        this.linkedTicket = null;
        break;
    }
  }

  hasLinkedEntities(): boolean {
    return !!(this.linkedJob || this.linkedCustomer || this.linkedTicket);
  }

  // ==================== LABELS ====================

  openLabelPicker(): void {
    this.showLabelPicker = !this.showLabelPicker;
    if (this.showLabelPicker) {
      this.closeAllPickers('label');
    }
  }

  closeLabelPicker(): void {
    this.showLabelPicker = false;
  }

  toggleLabel(label: any): void {
    const index = this.selectedLabels.findIndex((l: any) => l.id === label.id);
    if (index > -1) {
      this.selectedLabels.splice(index, 1);
    } else {
      this.selectedLabels.push(label);
    }
  }

  removeLabel(label: any): void {
    this.selectedLabels = this.selectedLabels.filter((l: any) => l.id !== label.id);
  }

  isLabelSelected(label: any): boolean {
    return this.selectedLabels.some((l: any) => l.id === label.id);
  }

  // ==================== SIGNATURE ====================

  toggleSignatureSelector(): void {
    this.showSignatureSelector = !this.showSignatureSelector;
    if (this.showSignatureSelector) {
      this.closeAllPickers('signature');
    }
  }

  onSignatureSelected(signature: any | null): void {
    this.currentSignature = signature;
    this.appendSignatureToBody();
    this.showSignatureSelector = false;
  }

  private appendSignatureToBody(): void {
    const signatureDelimiter = '\n\n---\n';

    // Remove existing signature if any (simple approach)
    let body = this.composeForm.body || '';
    const delimiterIndex = body.lastIndexOf(signatureDelimiter);
    if (delimiterIndex !== -1) {
      body = body.substring(0, delimiterIndex);
    }

    // Append new signature if exists
    if (this.currentSignature?.contentHtml) {
      // Convert HTML to plain text for textarea
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = this.currentSignature.contentHtml;
      const plainText = tempDiv.textContent || tempDiv.innerText || '';
      body = body.trim() + signatureDelimiter + plainText;
    }

    this.composeForm.body = body;
  }

  getCurrentSignatureName(): string {
    return this.currentSignature?.name || 'No Signature';
  }

  // ==================== SCHEDULE SEND ====================

  openSchedulePicker(): void {
    this.showSchedulePicker = !this.showSchedulePicker;
    if (this.showSchedulePicker) {
      this.closeAllPickers('schedule');
      // Set default time to next hour
      const now = new Date();
      now.setHours(now.getHours() + 1, 0, 0, 0);
      if (!this.scheduledDate) {
        this.scheduledDate = now;
        this.scheduledTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      }
    }
  }

  closeSchedulePicker(): void {
    this.showSchedulePicker = false;
  }

  confirmSchedule(): void {
    if (this.scheduledDate && this.scheduledTime) {
      const [hours, minutes] = this.scheduledTime.split(':').map(Number);
      const scheduledDateTime = new Date(this.scheduledDate);
      scheduledDateTime.setHours(hours, minutes, 0, 0);

      // Validate that scheduled time is in the future
      if (scheduledDateTime <= new Date()) {
        this.toasterService.showWarning('Please select a future date and time');
        return;
      }

      this.scheduledDate = scheduledDateTime;
      this.isScheduled = true;
      this.showSchedulePicker = false;
      this.toasterService.showSuccessMessage(`Scheduled for ${scheduledDateTime.toLocaleDateString()} at ${this.scheduledTime}`);
    }
  }

  clearSchedule(): void {
    this.scheduledDate = null;
    this.scheduledTime = '';
    this.isScheduled = false;
  }

  scheduleQuick(option: 'tomorrow_morning' | 'tomorrow_afternoon' | 'monday_morning'): void {
    const now = new Date();
    let targetDate = new Date();

    switch (option) {
      case 'tomorrow_morning':
        targetDate.setDate(now.getDate() + 1);
        targetDate.setHours(8, 0, 0, 0);
        break;
      case 'tomorrow_afternoon':
        targetDate.setDate(now.getDate() + 1);
        targetDate.setHours(13, 0, 0, 0);
        break;
      case 'monday_morning':
        const daysUntilMonday = (8 - now.getDay()) % 7 || 7;
        targetDate.setDate(now.getDate() + daysUntilMonday);
        targetDate.setHours(8, 0, 0, 0);
        break;
    }

    this.scheduledDate = targetDate;
    this.scheduledTime = `${String(targetDate.getHours()).padStart(2, '0')}:${String(targetDate.getMinutes()).padStart(2, '0')}`;
    this.isScheduled = true;
    this.showSchedulePicker = false;
    this.toasterService.showSuccessMessage(`Scheduled for ${targetDate.toLocaleDateString()} at ${this.scheduledTime}`);
  }

  get sendButtonText(): string {
    return this.isScheduled ? 'Schedule' : 'Send';
  }

  // ==================== DRAFT ====================

  saveDraft(): void {
    if (!this.activeAccount) {
      this.toasterService.showWarning('No active email account');
      return;
    }

    this.isSavingDraft = true;

    const toRecipients = this.parseRecipients(this.composeForm.to);
    const ccRecipients = this.showCC ? this.parseRecipients(this.composeForm.cc) : [];
    const bccRecipients = this.showBCC ? this.parseRecipients(this.composeForm.bcc) : [];

    const toEmails = toRecipients.map((r: any) => r.email).filter(Boolean);
    const ccEmails = ccRecipients.map((r: any) => r.email).filter(Boolean);
    const bccEmails = bccRecipients.map((r: any) => r.email).filter(Boolean);

    const formData = new FormData();
    formData.append('email_account_id', this.activeAccount.id.toString());
    formData.append('subject', this.composeForm.subject || '');
    formData.append('body', this.composeForm.body?.replace(/\n/g, '<br>') || '');

    if (toEmails.length > 0) {
      formData.append('to', toEmails.join(','));
    }
    if (ccEmails.length > 0) {
      formData.append('cc', ccEmails.join(','));
    }
    if (bccEmails.length > 0) {
      formData.append('bcc', bccEmails.join(','));
    }

    if (this.attachments && this.attachments.length > 0) {
      this.attachments.forEach((file: File, index: number) => {
        formData.append(`attachments[${index}]`, file, file.name);
      });
    }

    this.emailDataService.saveDraft(formData).subscribe({
      next: () => {
        this.isSavingDraft = false;
        this.toasterService.showSuccessMessage('Draft saved');
        this.viewMode = 'list';
        this.resetComposeState();
      },
      error: (err) => {
        this.isSavingDraft = false;
        this.toasterService.showError('Failed to save draft');
        console.error('Save draft error:', err);
      }
    });
  }

  // ==================== HELPER: CLOSE ALL PICKERS ====================

  private closeAllPickers(except?: 'emoji' | 'entity' | 'label' | 'signature' | 'schedule'): void {
    if (except !== 'emoji') this.showEmojiPicker = false;
    if (except !== 'entity') this.showEntityLinkPanel = false;
    if (except !== 'label') this.showLabelPicker = false;
    if (except !== 'signature') this.showSignatureSelector = false;
    if (except !== 'schedule') this.showSchedulePicker = false;
  }

  // ==================== REPLY/FORWARD ACTIONS ====================

  replyToEmail(email: IQuickEmail, event: Event): void {
    event.stopPropagation();
    this.openCompose('reply', email);
  }

  replyAllToEmail(email: IQuickEmail, event: Event): void {
    event.stopPropagation();
    this.openCompose('replyAll', email);
  }

  forwardEmail(email: IQuickEmail, event: Event): void {
    event.stopPropagation();
    this.openCompose('forward', email);
  }

  markAsRead(email: IQuickEmail, event: Event): void {
    event.stopPropagation();
    this.emailSidebarService.markAsRead(email.id);
  }

  markAsUnread(email: IQuickEmail, event: Event): void {
    event.stopPropagation();
    this.emailSidebarService.markAsUnread(email.id);
  }

  toggleRead(email: IQuickEmail, event: Event): void {
    event.stopPropagation();
    if (email.isRead) {
      this.emailSidebarService.markAsUnread(email.id);
    } else {
      this.emailSidebarService.markAsRead(email.id);
    }
  }

  toggleFlag(email: IQuickEmail, event: Event): void {
    event.stopPropagation();
    this.emailSidebarService.toggleFlag(email.id);
  }

  deleteEmail(email: IQuickEmail, event: Event): void {
    event.stopPropagation();
    this.emailSidebarService.deleteEmail(email.id);
    // Go back to list since the email no longer exists
    if (this.viewMode === 'reading') {
      this.selectedEmail = null;
      this.threadMessages = [];
      this.viewMode = 'list';
    }
  }

  markAllAsRead(): void {
    this.emailSidebarService.markAllAsRead();
  }

  // ==================== NOTIFICATION ACTIONS ====================

  dismissNotification(notification: IEmailNotification, event: Event): void {
    event.stopPropagation();
    this.emailSidebarService.dismissNotification(notification.id);
  }

  onNotificationClick(notification: IEmailNotification): void {
    this.emailSidebarService.markNotificationAsRead(notification.id);
    // Navigate to the related email
    this.router.navigate(['/email'], { queryParams: { emailId: notification.emailId } });
    this.closeSidebar();
  }

  clearAllNotifications(): void {
    this.emailSidebarService.clearAllNotifications();
  }

  // ==================== NAVIGATION ====================

  navigateToEmail(email: IQuickEmail): void {
    this.router.navigate(['/email'], { queryParams: { emailId: email.id } });
    this.closeSidebar();
  }

  navigateToInbox(): void {
    this.router.navigate(['/email']);
    this.closeSidebar();
  }

  composeNewEmail(): void {
    this.openCompose('compose');
  }

  // ==================== SIDEBAR ACTIONS ====================

  closeSidebar(): void {
    // Reset reading pane and compose state when closing
    this.selectedEmail = null;
    this.resetComposeState();
    this.viewMode = 'list';
    this.onClose.emit();
  }

  refreshEmails(): void {
    this.emailSidebarService.refreshEmails();
  }

  // ==================== ACCOUNT MANAGEMENT ====================

  toggleAccountSelector(): void {
    this.showAccountSelector = !this.showAccountSelector;
  }

  selectAccount(account: IUserAccount): void {
    this.emailSidebarService.switchAccount(account);
    this.showAccountSelector = false;
    // Reset folder when account changes
    this.activeFolder = null;
    this.expandedFolders.clear();
    this.emailSidebarService.setActiveFolder(null);
  }

  closeAccountSelector(): void {
    this.showAccountSelector = false;
  }

  // ==================== FOLDER NAVIGATION ====================

  /**
   * Get folders for the current active account
   */
  get accountFolders(): IEmailFolder[] {
    return this.activeAccount?.folders || [];
  }

  /**
   * Get root-level folders (PID === null)
   */
  get rootFolders(): IEmailFolder[] {
    return this.accountFolders.filter(f => f.PID === null);
  }

  /**
   * Get child folders for a given parent
   */
  getChildFolders(parentId: number): IEmailFolder[] {
    return this.accountFolders.filter(f => f.PID === parentId);
  }

  /**
   * Toggle folder tree visibility
   */
  toggleFolderTree(): void {
    this.showFolderTree = !this.showFolderTree;
  }

  /**
   * Select a folder to filter emails
   */
  selectFolder(folder: IEmailFolder | null, event: Event): void {
    event.stopPropagation();
    this.activeFolder = folder;
    this.emailSidebarService.setActiveFolder(folder);
  }

  /**
   * Toggle folder expand/collapse state
   */
  toggleFolderExpand(folder: IEmailFolder, event: Event): void {
    event.stopPropagation();
    if (this.expandedFolders.has(folder.ID)) {
      this.expandedFolders.delete(folder.ID);
    } else {
      this.expandedFolders.add(folder.ID);
    }
  }

  /**
   * Check if folder is expanded
   */
  isFolderExpanded(folder: IEmailFolder): boolean {
    return this.expandedFolders.has(folder.ID);
  }

  /**
   * Get Material icon for folder type
   */
  getFolderIcon(folder: IEmailFolder): string {
    // Use FOLDER_ICONS mapping or fallback to generic folder icon
    return (FOLDER_ICONS as any)[folder.Name] || folder.icon || 'folder';
  }

  // ==================== HELPER METHODS ====================

  /**
   * Format CC recipients array for display
   */
  formatCcRecipients(recipients: Array<{ name: string; email: string }>): string {
    if (!recipients || recipients.length === 0) return '';
    return recipients.map(r => r.name ? `${r.name} <${r.email}>` : r.email).join(', ');
  }

  // ==================== ATTACHMENT ACTIONS ====================

  /**
   * Preview attachment using shared preview components
   */
  previewAttachment(attachment: IEmailAttachment, event: Event): void {
    event.stopPropagation();

    const fileType = this.getAttachmentFileType(attachment);

    // Build data object expected by shared preview components
    const previewData = {
      url: attachment.url,
      file_url: attachment.url,
      file_name: attachment.name,
      file_type: this.getFileExtension(attachment.name),
      original_name: attachment.name,
      id: attachment.id
    };

    switch (fileType) {
      case 'image':
        // Use DocumentImageAnnotatorComponent (zoom, rotate, pan, annotate)
        this.dialog.open(DocumentImageAnnotatorComponent, {
          data: previewData,
          width: '92vw',
          maxWidth: '92vw',
          height: '88vh',
          panelClass: 'document-image-annotator-panel'
        });
        break;

      case 'pdf':
        // Use shared PdfPreviewComponent (page navigation, print)
        this.dialog.open(PdfPreviewComponent, {
          data: previewData,
          width: '90vw',
          height: '90vh',
          panelClass: 'pdf-preview-dialog'
        });
        break;

      case 'word':
        // Use TextPreviewComponent for Word documents (.doc, .docx)
        this.lazyPreview.openTextPreview({
          data: previewData,
          width: '90vw',
          height: '90vh',
          panelClass: 'text-preview-dialog'
        });
        break;

      case 'excel':
        // Use CsvPreviewComponent for Excel files (.xlsx, .xls, .csv)
        this.lazyPreview.openCsvPreview({
          data: previewData,
          width: '90vw',
          height: '90vh',
          panelClass: 'csv-preview-dialog'
        });
        break;

      case 'audio':
        // Use AudioPreviewComponent (play, pause, timeline, volume)
        this.dialog.open(AudioPreviewComponent, {
          data: previewData,
          width: '500px',
          panelClass: 'audio-preview-dialog'
        });
        break;

      case 'video':
        // Use VideoPreviewComponent (play, pause, fullscreen, timeline)
        this.dialog.open(VideoPreviewComponent, {
          data: previewData,
          width: '90vw',
          height: '90vh',
          panelClass: 'video-preview-dialog'
        });
        break;

      default:
        // For other file types, open in new tab
        window.open(attachment.url, '_blank');
    }
  }

  /**
   * Download single attachment
   */
  downloadAttachment(attachment: IEmailAttachment, event: Event): void {
    event.stopPropagation();

    // Create download link
    const link = document.createElement('a');
    link.href = attachment.url;
    link.download = attachment.name;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Download all attachments
   */
  downloadAllAttachments(event: Event): void {
    event.stopPropagation();
    if (this.selectedEmail?.attachments) {
      this.selectedEmail.attachments.forEach(att => {
        this.downloadAttachment(att, event);
      });
    }
  }

  /**
   * Get file type category based on MIME type
   */
  getAttachmentFileType(attachment: IEmailAttachment): string {
    const type = attachment.type.toLowerCase();
    const name = attachment.name.toLowerCase();
    const ext = this.getFileExtension(name);

    // Check by MIME type first
    if (type.includes('image/')) return 'image';
    if (type.includes('pdf')) return 'pdf';
    if (type.includes('word') || type.includes('msword') ||
        type.includes('wordprocessingml')) return 'word';
    if (type.includes('excel') || type.includes('spreadsheet') ||
        type.includes('csv')) return 'excel';
    if (type.includes('audio/')) return 'audio';
    if (type.includes('video/')) return 'video';

    // Fallback to extension check
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext)) return 'image';
    if (ext === 'pdf') return 'pdf';
    if (['doc', 'docx', 'rtf', 'txt', 'md', 'markdown', 'json', 'xml', 'yaml', 'yml', 'html', 'htm', 'ini', 'conf', 'log'].includes(ext)) return 'word';
    if (['xls', 'xlsx', 'xlsm', 'ods', 'csv', 'tsv'].includes(ext)) return 'excel';
    if (['mp3', 'wav', 'aac', 'wma', 'ogg', 'oga', 'm4a', 'm4b', 'flac', 'opus', 'amr', 'weba'].includes(ext)) return 'audio';
    if (['mp4', 'm4v', 'mkv', 'wmv', 'mov', 'avi', 'mpg', 'mpeg', 'webm', '3gp', '3g2', 'ts', 'mts', 'm2ts', 'flv', 'ogv'].includes(ext)) return 'video';

    return 'other';
  }

  /**
   * Check if attachment is previewable (images, PDFs, Word, Excel, Audio, Video)
   */
  isPreviewable(attachment: IEmailAttachment): boolean {
    const fileType = this.getAttachmentFileType(attachment);
    return ['image', 'pdf', 'word', 'excel', 'audio', 'video'].includes(fileType);
  }

  /**
   * Get file extension from filename
   */
  getFileExtension(filename: string): string {
    return filename.split('.').pop()?.toLowerCase() || '';
  }

  /**
   * Get total attachments size for selected email
   */
  getTotalAttachmentsSize(): number {
    if (!this.selectedEmail?.attachments) return 0;
    return this.selectedEmail.attachments.reduce((sum, att) => sum + att.size, 0);
  }

  getTabCount(tab: EmailSidebarTab): number {
    switch (tab) {
      case 'inbox':
        return this.emails.length;
      case 'unread':
        return this.unreadCount;
      case 'flagged':
        return this.flaggedCount;
      case 'notifications':
        return this.notificationCount;
      default:
        return 0;
    }
  }

  getPriorityClass(priority: string): string {
    switch (priority) {
      case 'high':
        return 'priority-high';
      case 'low':
        return 'priority-low';
      default:
        return '';
    }
  }

  trackByEmailId(index: number, email: IQuickEmail): number {
    return email.id;
  }

  trackByNotificationId(index: number, notification: IEmailNotification): number {
    return notification.id;
  }

  // ==================== CLICK OUTSIDE ====================

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // Check if click is outside the sidebar
    const clickedElement = event.target as HTMLElement;
    const isInsideSidebar = this.elementRef.nativeElement.contains(clickedElement);
    const isInsidePanel = clickedElement.closest('.email-sidebar-panel');
    const isMailTrigger = clickedElement.closest('.mail-trigger');

    // Exclude clicks from MatDialog/CDK overlays (attachment previews, etc.)
    const isInsideCdkOverlay = clickedElement.closest('.cdk-overlay-container');

    // Close account selector if clicking outside of it
    if (this.showAccountSelector) {
      const isInsideAccountSelector = clickedElement.closest('.account-selector-dropdown');
      const isAccountTrigger = clickedElement.closest('.account-selector-trigger');
      if (!isInsideAccountSelector && !isAccountTrigger) {
        this.showAccountSelector = false;
      }
    }

    // Only close sidebar if click is truly outside (not in dialogs)
    if (this.isOpen && !isInsideSidebar && !isInsidePanel && !isMailTrigger && !isInsideCdkOverlay) {
      this.closeSidebar();
    }
  }

  // ==================== KEYBOARD SHORTCUTS ====================

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (this.isOpen) {
      event.preventDefault();
      this.closeSidebar();
    }
  }
}
